/**
 * API Configuration
 * 
 * By default, uses the current page hostname with port 3001.
 * Can be overridden via environment variables:
 * - VITE_API_HOST: API hostname (e.g., "api.example.com")
 * - VITE_API_PORT: API port (e.g., "3001")
 * - VITE_API_PROTOCOL: API protocol (e.g., "https")
 */

function getApiBaseUrl(): string {
  // Check for full URL override first
  const fullUrl = import.meta.env.VITE_API_URL;
  if (fullUrl) {
    return fullUrl;
  }

  // Get current page info
  const currentProtocol = typeof window !== 'undefined' ? window.location.protocol : 'http:';
  const currentHostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';

  // Use env vars or defaults based on current page
  const protocol = import.meta.env.VITE_API_PROTOCOL || currentProtocol.replace(':', '');
  const host = import.meta.env.VITE_API_HOST || currentHostname;
  const port = import.meta.env.VITE_API_PORT || '3001';

  return `${protocol}://${host}:${port}`;
}

// Export the base URL - computed once when module loads
export const API_BASE_URL = getApiBaseUrl();

// Helper to build API endpoints
export function apiUrl(path: string): string {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE_URL}${cleanPath}`;
}

/** Same key as `I18nProvider` (`src/i18n/I18nProvider.tsx`). */
const I18N_STORAGE_KEY = 'app_locale';

const SUPPORTED_APP_LOCALES = ['en', 'ro', 'de', 'es', 'fr'] as const;
export type AppLocale = (typeof SUPPORTED_APP_LOCALES)[number];

export function getStoredAppLocale(): AppLocale | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const raw = window.localStorage.getItem(I18N_STORAGE_KEY);
    if (
      raw &&
      (SUPPORTED_APP_LOCALES as readonly string[]).includes(raw)
    ) {
      return raw as AppLocale;
    }
  } catch {
    // ignore storage errors
  }
  return undefined;
}

/** Locale header for the backend i18n hook (optional if absent). */
export function apiLocaleHeaders(): Record<string, string> {
  const loc = getStoredAppLocale();
  return loc ? { 'X-App-Locale': loc } : {};
}

/** Authorization + `X-App-Locale` for API `fetch` calls. */
export function apiHeaders(token?: string | null): Record<string, string> {
  return {
    ...apiLocaleHeaders(),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

